// ** Next Imports
import Head from 'next/head'
import { Router } from 'next/router'
import { createPortal } from 'react-dom'

// ** Store Imports
// import { store } from 'src/store'
import { store } from 'src/store/state'
import { Provider } from 'react-redux'

// ** Loader Import
import NProgress from 'nprogress'

// ** Emotion Imports
import { CacheProvider } from '@emotion/react'

// ** Config Imports

import { defaultACLObj } from 'src/configs/acl'
import themeConfig from 'src/configs/themeConfig'

//** Third Party Import
import { Toaster } from 'react-hot-toast'

// ** Component Imports
import UserLayout from 'src/layouts/UserLayout'
import AclGuard from 'src/@core/components/auth/AclGuard'
import ThemeComponent from 'src/@core/theme/ThemeComponent'
import AuthGuard from 'src/@core/components/auth/AuthGuard'
import GuestGuard from 'src/@core/components/auth/GuestGuard'

// ** Spinner Import
import Spinner from 'src/@core/components/spinner'

// ** Terms Acceptance
import TermsAcceptanceDialog from 'src/views/apps/shared/dialogs/TermsAcceptanceDialog'

// ** Contexts
import { AuthProvider } from 'src/context/AuthContext'
import { SegmentProvider } from 'src/context/SegmentContext'
import { PromotionalCampaignProvider } from 'src/context/PromotionalCampaignContext'
import { SettingsConsumer, SettingsProvider } from 'src/@core/context/settingsContext'

// ** Styled Components
import ReactHotToast from 'src/@core/styles/libs/react-hot-toast'

// ** Utils Imports
import { createEmotionCache } from 'src/@core/utils/create-emotion-cache'

// ** Prismjs Styles
import 'prismjs'
import 'prismjs/themes/prism-tomorrow.css'
import 'prismjs/components/prism-jsx'
import 'prismjs/components/prism-tsx'

// ** React Perfect Scrollbar Style
import 'react-perfect-scrollbar/dist/css/styles.css'
import 'src/iconify-bundle/icons-bundle-react'

// ** Global css styles
import '../styles/globals.css'
import '../styles/query-style.css'
import '../styles/whatsapp.css'
import '../styles/beefree.css'
import '../views/apps/engagement/marketing-automation/journey-builder/jb-styles.css'

// import '../../styles/whatspp.css'
import { AudienceProvider } from 'src/context/AudienceContext'
import { CustomReportProvider } from 'src/context/CustomReportsContext'
import { useEffect, useState } from 'react'
import { DashboardProvider } from 'src/context/DashBoardContext'
import { TemplateProvider } from 'src/context/TemplateContext'
import { JourneyProvider } from 'src/context/JourneyContext'
import { TransactionalCampaignProvider } from 'src/context/TransactionalCampaignContext'
import { CampaignProvider } from 'src/context/CampaignContext'
import { DiagramServiceProvider } from 'src/context/DiagramServiceContext'
import { ToastProvider } from 'src/@core/components/custom-Toast/customToast'
import { CampaignAudienceDashboardProvider } from 'src/context/CampaignAudienceDashboadContext'
import { useFullscreenContainer } from 'src/hooks/useFullscreenContainer'
import { MediaLibraryProvider } from 'src/context/MediaLibraryContext'

const clientSideEmotionCache = createEmotionCache()

// ** Pace Loader
if (themeConfig.routingLoader) {
  Router.events.on('routeChangeStart', () => {
    NProgress.start()
  })
  Router.events.on('routeChangeError', () => {
    NProgress.done()
  })
  Router.events.on('routeChangeComplete', () => {
    NProgress.done()
  })
}

const Guard = ({ children, authGuard, guestGuard }) => {
  if (guestGuard) {
    return <GuestGuard fallback={<Spinner />}>{children}</GuestGuard>
  } else if (!guestGuard && !authGuard) {
    return <>{children}</>
  } else {
    return <AuthGuard fallback={<Spinner />}>{children}</AuthGuard>
  }
}

const FullscreenAwareToaster = ({ position }) => {
  const container = useFullscreenContainer()
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  const toaster = (
    <ReactHotToast>
      <Toaster position={position} toastOptions={{ className: 'react-hot-toast' }} />
    </ReactHotToast>
  )

  if (!mounted || !container || container === document.body) {
    return toaster
  }

  return createPortal(toaster, container)
}

// ** Configure JSS & ClassName
const App = props => {
  const { Component, emotionCache = clientSideEmotionCache, pageProps } = props

  // Variables
  const contentHeightFixed = Component.contentHeightFixed ?? false

  const getLayout =
    Component.getLayout ?? (page => <UserLayout contentHeightFixed={contentHeightFixed}>{page}</UserLayout>)

  const setConfig = Component.setConfig ?? undefined

  const authGuard = Component.authGuard ?? true

  const guestGuard = Component.guestGuard ?? false

  const aclAbilities = Component.acl ?? defaultACLObj

  return (
    <Provider store={store}>
      <CacheProvider value={emotionCache}>
        <Head>
          <title>
            cXpify
            {/* {`${themeConfig.templateName} - Material Design React Admin Template`} */}
          </title>
          <meta
            name='description'
            content='Totient CXP'

          // {`${themeConfig.templateName} – Material Design React Admin Dashboard Template – is the most developer friendly & highly customizable Admin Dashboard Template based on MUI v5.`}
          />
          <meta name='keywords' content='Material Design, MUI, Admin Template, React Admin Template' />
          <meta name='viewport' content='initial-scale=1, width=device-width' />
        </Head>
        <ToastProvider>
          <CampaignProvider>
            <AuthProvider>
              <JourneyProvider>
                <DiagramServiceProvider>
                  <TemplateProvider>
                    <MediaLibraryProvider>
                      <PromotionalCampaignProvider>
                        <TransactionalCampaignProvider>
                          <AudienceProvider>
                            <SegmentProvider>
                              <CustomReportProvider>
                                <DashboardProvider>
                                  <CampaignAudienceDashboardProvider>
                                    <SettingsProvider {...(setConfig ? { pageSettings: setConfig() } : {})}>
                                      <SettingsConsumer>
                                        {({ settings }) => {
                                          return (
                                            <ThemeComponent settings={settings}>
                                              <Guard authGuard={authGuard} guestGuard={guestGuard}>
                                                <AclGuard
                                                  aclAbilities={aclAbilities}
                                                  guestGuard={guestGuard}
                                                  authGuard={authGuard}
                                                >
                                                  {getLayout(<Component {...pageProps} />)}
                                                </AclGuard>
                                              </Guard>
                                              <FullscreenAwareToaster position={settings.toastPosition} />
                                              <TermsAcceptanceDialog />
                                            </ThemeComponent>
                                          )
                                        }}
                                      </SettingsConsumer>
                                    </SettingsProvider>
                                  </CampaignAudienceDashboardProvider>
                                </DashboardProvider>
                              </CustomReportProvider>
                            </SegmentProvider>
                          </AudienceProvider>
                        </TransactionalCampaignProvider>
                      </PromotionalCampaignProvider>
                    </MediaLibraryProvider>
                  </TemplateProvider>
                </DiagramServiceProvider>
              </JourneyProvider>
            </AuthProvider>
          </CampaignProvider>
        </ToastProvider>
      </CacheProvider>
    </Provider>
  )
}

export default App
